import * as React from 'react';
import { DateRangeValue, message } from '@traceair/tds';
import { ScanCostWithDateType, InvoicingSection, InvoicingCostItem, ItemStatus } from '../types';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  numericOnly,
  buildConsultantGcpRateInfo,
  buildStableItemIds,
  getGcpItemAmount,
  getCategoryGroupId,
  isGcpRateFullySet,
  toGcpCostBasis,
  toGcpExtraCosts,
  collectMissionRefIds,
  buildTimeMaterialsDayEntries,
  sumGcpExtraCosts,
} from '../InvoicingUtils';
import getPilotApiService from '../../../../api_services/PilotApiService';
import type { AssociatedMissionPayload, TimeMaterialsEntryPayload } from '../../../../api_services/types';
import Logger from '../../../../libs/logger/Logger';
import { useTimeMaterialsDateGroups, toDateKey } from './useTimeMaterialsDateGroups';
import { useGcpItemPrices } from './useGcpItemPrices';
import { useGcpMaterialEntries } from './useGcpMaterialEntries';

// ─── Module-level helpers ─────────────────────────────────────────────────────

function sectionTotal(section: InvoicingSection): number {
  return section.items.filter((i) => i.status !== 'failed').reduce((s, i) => s + (i.amount ?? 0), 0);
}

const RATE_UNIT: Record<string, [string, string]> = {
  pano: ['pano', 'panos'],
  gcp_services: ['hour', 'hours'],
  scan: ['scan', 'scans'],
};

function buildRateInfo(category: string, cost: number, itemCount: number): string | undefined {
  if (itemCount < 1) return undefined;
  // A single pano still shows its per-pano rate; other categories only show a rate breakdown
  // once there's more than one item (a single scan's rate is just its own displayed amount).
  if (itemCount === 1 && category !== 'pano') return undefined;
  const rate = parseFloat((cost / itemCount).toFixed(2));
  const [unit, units] = RATE_UNIT[category] ?? ['item', 'items'];
  const countLabel = itemCount === 1 ? unit : units;
  return `${itemCount} ${countLabel} · $${rate}/${unit}`;
}

function isValidPaymentUrl(url: string): boolean {
  try {
    return ['http:', 'https:'].includes(new URL(url.trim()).protocol);
  } catch {
    return false;
  }
}

function deriveSections(costs: ScanCostWithDateType[]): InvoicingSection[] {
  const grouped = new Map<string, ScanCostWithDateType[]>();
  for (const item of costs) {
    const category = getCategoryGroupId(item.category);
    const list = grouped.get(category) ?? [];
    list.push(item);
    grouped.set(category, list);
  }
  return Array.from(grouped.entries())
    .sort(([a], [b]) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
    })
    .map(([category, items]) => {
      const sorted = [...items].sort((a, b) => b.flightDate.getTime() - a.flightDate.getTime());
      // Keyed by record id, not by position: this list is re-derived on every change to the
      // selected period, and per-item price/materials state has to follow the mission it was
      // entered against. See buildStableItemIds.
      const ids = buildStableItemIds(
        category,
        sorted.map((item) => item.airtableRecordId),
      );
      const invoicingItems: InvoicingCostItem[] = sorted.map((item, idx) => ({
        id: ids[idx],
        gcpServiceId: item.gcpServiceId,
        airtableRecordId: item.airtableRecordId,
        airtableProjectRecordId: item.airtableProjectRecordId,
        flightDateIso: toDateKey(item.flightDate),
        date: item.flightDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        siteName: item.project,
        customer: item.account,
        category: item.category,
        // Carried through because the submit payload sends it per line item: without it the
        // optional field falls back to 1 and a 12-panorama mission is billed as a single pano.
        itemCount: item.itemCount,
        rateInfo:
          category === 'gcp_services' && !item.isFailed
            ? buildConsultantGcpRateInfo(item.consultantGcpServiceHourlyRate, item.consultantGcpServiceTimeInHours)
            : buildRateInfo(category, item.cost, item.itemCount),
        status: (item.isFailed ? 'failed' : item.unprocessed ? 'on_review' : 'billable') as ItemStatus,
        amount: getGcpItemAmount(item.cost, item.consultantGcpServiceHourlyRate, item.consultantGcpServiceTimeInHours),
        consultantGcpServiceHourlyRate: item.consultantGcpServiceHourlyRate,
        consultantGcpServiceTimeInHours: item.consultantGcpServiceTimeInHours,
      }));
      return {
        id: category,
        label: CATEGORY_LABELS[category] ?? category,
        items: invoicingItems,
        total: invoicingItems.filter((i) => i.status !== 'failed').reduce((s, i) => s + (i.amount ?? 0), 0),
      };
    });
}

// ─── Hook ────────────────────────────────────────────────────────────────────

type UseInvoiceFormArgs = {
  scanCosts: ScanCostWithDateType[] | null;
  dateRange: DateRangeValue | undefined;
  isFixedFee: boolean;
  savedBankDetails?: string;
  onBack?: () => void;
  onSubmit?: (data: unknown) => Promise<void> | void;
};

export function useInvoiceForm({
  scanCosts,
  dateRange,
  isFixedFee,
  savedBankDetails,
  onBack,
  onSubmit,
}: UseInvoiceFormArgs) {
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const [showLeaveDialog, setShowLeaveDialog] = React.useState(false);
  const [otherItems, setOtherItems] = React.useState([{ id: '1', label: '', price: '' }]);
  const {
    entries: gcpMaterialEntries,
    handlers: gcpMaterialsHandlers,
    hasEntries: hasGcpMaterialEntries,
    total: gcpMaterialsTotal,
  } = useGcpMaterialEntries();
  const [equipmentLeasingPrice, setEquipmentLeasingPrice] = React.useState('');
  const [pdfFile, setPdfFile] = React.useState<File | undefined>(undefined);
  const [isPdfUploading, setIsPdfUploading] = React.useState(false);
  const [uploadedPdfLink, setUploadedPdfLink] = React.useState<string | undefined>(undefined);
  const [paymentMethod, setPaymentMethodRaw] = React.useState<'link' | 'bank'>('link');
  const [paymentLink, setPaymentLink] = React.useState('');
  const [bankDetails, setBankDetails] = React.useState('');
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [submittedOtherIds, setSubmittedOtherIds] = React.useState<ReadonlySet<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const defaultPaymentMethodRef = React.useRef<'link' | 'bank'>('link');
  const defaultBankDetailsRef = React.useRef('');

  // Once the pilot's previously saved bank details load, default the form to them: most
  // pilots reuse the same account details, so pre-select "Bank account details" instead of "Payment
  // link". Only applies while the fields are still untouched, so it never clobbers user input.
  React.useEffect(() => {
    if (!savedBankDetails) return;
    setPaymentMethodRaw((prev) => (prev === 'link' ? 'bank' : prev));
    setBankDetails((prev) => (prev === '' ? savedBankDetails : prev));
    defaultPaymentMethodRef.current = 'bank';
    defaultBankDetailsRef.current = savedBankDetails;
  }, [savedBankDetails]);

  const isMount = React.useRef(true);
  const uploadRequestRef = React.useRef(0);
  React.useEffect(
    () => () => {
      isMount.current = false;
    },
    [],
  );

  // Derived: filtered costs
  const filteredCosts = React.useMemo(() => {
    if (!scanCosts) return null;
    const [start, end] = dateRange ?? [];
    if (!start && !end) return scanCosts;
    return scanCosts.filter((item) => {
      const ms = item.flightDate.getTime();
      if (start && ms < start.startOf('day').valueOf()) return false;
      if (end && ms > end.endOf('day').valueOf()) return false;
      return true;
    });
  }, [scanCosts, dateRange]);

  // Derived: cost sections (fixed-fee mode)
  const costSections = React.useMemo(() => {
    const sections = filteredCosts ? deriveSections(filteredCosts) : [];
    if (!sections.some((s) => s.id === 'gcp_services')) {
      sections.push({ id: 'gcp_services', label: CATEGORY_LABELS['gcp_services'], items: [] });
      sections.sort((a, b) => {
        const ai = CATEGORY_ORDER.indexOf(a.id);
        const bi = CATEGORY_ORDER.indexOf(b.id);
        return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
      });
    }
    return sections;
  }, [filteredCosts]);

  // Derived: T&M date groups, totals, price setter, and per-item GCP price overrides
  const {
    timeMaterialsDateGroups,
    timeMaterialsDatePrices,
    timeMaterialsTotal,
    setDatePrice,
    gcpItemPrices: tmGcpItemPrices,
    updateGcpItemPrice: updateTmGcpItemPrice,
    getGcpItemPrice: getTmGcpItemPrice,
    isGcpItemPricesDirty: isTmGcpItemPricesDirty,
    gcpDefaultAmountById: tmGcpDefaultAmountById,
  } = useTimeMaterialsDateGroups({ isFixedFee, filteredCosts });

  // Derived: GCP service items (fixed-fee mode) — priced individually rather than as one
  // section-level total, since each item now carries its own backend-calculated rate.
  const gcpPriceableItems = React.useMemo(
    () =>
      (costSections.find((s) => s.id === 'gcp_services')?.items ?? [])
        .filter((item) => item.status !== 'failed')
        .map((item) => ({
          id: item.id,
          defaultAmount: item.amount ?? 0,
          // Rate × hours rows are read-only here (see SubmitInvoice.tsx, which renders no input for
          // them), so they bill the calculated amount and never an override.
          locked: isGcpRateFullySet(item.consultantGcpServiceHourlyRate, item.consultantGcpServiceTimeInHours),
        })),
    [costSections],
  );

  const {
    prices: gcpItemPrices,
    updatePrice: updateGcpItemPrice,
    getPrice: getGcpItemPrice,
    isDirty: isGcpItemPricesDirty,
    total: gcpServicesTotal,
  } = useGcpItemPrices(gcpPriceableItems);

  // Derived: billable/unprocessed date sets for calendar markers
  const { billableDates, unprocessedDates } = React.useMemo(() => {
    if (!scanCosts) return { billableDates: new Set<string>(), unprocessedDates: new Set<string>() };
    const billable = new Set<string>();
    const unprocessed = new Set<string>();
    for (const c of scanCosts) {
      const key = toDateKey(c.flightDate);
      if (c.isFailed) continue;
      // GCP services are always billable (blue marker) regardless of unprocessed state
      if (!c.unprocessed || c.category === 'gcp_services') {
        billable.add(key);
      } else {
        unprocessed.add(key);
      }
    }
    for (const key of billable) unprocessed.delete(key);
    return { billableDates: billable, unprocessedDates: unprocessed };
  }, [scanCosts]);

  // Totals
  const otherTotal = otherItems.reduce((s, i) => s + parseFloat(i.price || '0'), 0);
  const equipmentTotal = parseFloat(equipmentLeasingPrice || '0');
  const sectionsTotal = costSections
    .filter((s) => s.id !== 'gcp_services')
    .reduce((s, sec) => s + sectionTotal(sec), 0);

  const grandTotal = isFixedFee
    ? sectionsTotal + gcpServicesTotal + gcpMaterialsTotal + otherTotal - equipmentTotal
    : timeMaterialsTotal + gcpMaterialsTotal + otherTotal - equipmentTotal;

  // Validation flags
  const isNegativeTotal = isFixedFee && grandTotal < 0;
  const isZeroTotal = grandTotal === 0;
  const isUploadMissing = !uploadedPdfLink;
  const isPaymentLinkMissing = paymentMethod === 'link' && !paymentLink.trim();
  const isPaymentLinkInvalid = paymentMethod === 'link' && !!paymentLink.trim() && !isValidPaymentUrl(paymentLink);
  const isBankDetailsMissing = paymentMethod === 'bank' && !bankDetails.trim();
  const isSingleBlankItem = otherItems.length === 1 && !otherItems[0].label.trim() && !otherItems[0].price;
  const hasEmptyOtherLabel = !isSingleBlankItem && otherItems.some((i) => !i.label.trim());
  const displayTotal =
    grandTotal < 0 ? `-$${Math.abs(grandTotal).toLocaleString()}` : `$${grandTotal.toLocaleString()}`;

  const isDirty = React.useMemo(
    () =>
      pdfFile !== undefined ||
      paymentMethod !== defaultPaymentMethodRef.current ||
      paymentLink !== '' ||
      bankDetails !== defaultBankDetailsRef.current ||
      isGcpItemPricesDirty ||
      isTmGcpItemPricesDirty ||
      hasGcpMaterialEntries ||
      equipmentLeasingPrice !== '' ||
      Object.values(timeMaterialsDatePrices).some((p) => p.flightsCosts !== '' || p.mileage !== '') ||
      otherItems.some((i) => i.label !== '' || i.price !== ''),
    [
      pdfFile,
      paymentMethod,
      paymentLink,
      bankDetails,
      isGcpItemPricesDirty,
      isTmGcpItemPricesDirty,
      hasGcpMaterialEntries,
      equipmentLeasingPrice,
      timeMaterialsDatePrices,
      otherItems,
    ],
  );

  // beforeunload guard
  React.useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Handlers
  const setPaymentMethod = (m: 'link' | 'bank') => {
    setPaymentMethodRaw(m);
    setSubmitAttempted(false);
    setSubmittedOtherIds(new Set());
  };

  const toggleSection = (id: string) => setExpanded((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }));

  const addOtherItem = () => setOtherItems((prev) => [...prev, { id: String(Date.now()), label: '', price: '' }]);
  const removeOtherItem = (id: string) => setOtherItems((prev) => prev.filter((i) => i.id !== id));
  const updateOtherItemLabel = (id: string, value: string) =>
    setOtherItems((prev) => prev.map((i) => (i.id === id ? { ...i, label: value } : i)));
  const updateOtherItemPrice = (id: string, value: string) =>
    setOtherItems((prev) => prev.map((i) => (i.id === id ? { ...i, price: numericOnly(value) } : i)));

  async function handlePdfChange(file?: File) {
    const requestId = ++uploadRequestRef.current;
    setPdfFile(file);
    setUploadedPdfLink(undefined);
    if (file) {
      setIsPdfUploading(true);
      try {
        const filePath = await getPilotApiService().uploadInvoice(file);
        if (isMount.current && uploadRequestRef.current === requestId) setUploadedPdfLink(filePath);
      } catch (err) {
        Logger.error(err as Error | string);
        if (isMount.current && uploadRequestRef.current === requestId) setPdfFile(undefined);
      }
      if (isMount.current && uploadRequestRef.current === requestId) setIsPdfUploading(false);
    }
  }

  function handleBack() {
    if (isDirty) {
      setShowLeaveDialog(true);
    } else {
      onBack?.();
    }
  }

  function handleLeaveConfirm() {
    setShowLeaveDialog(false);
    onBack?.();
  }

  function handleLeaveCancel() {
    setShowLeaveDialog(false);
  }

  async function handleSubmit() {
    setSubmittedOtherIds(new Set(otherItems.map((i) => i.id)));
    setSubmitAttempted(true);
    if (
      isNegativeTotal ||
      isZeroTotal ||
      isUploadMissing ||
      isPaymentLinkMissing ||
      isPaymentLinkInvalid ||
      isBankDetailsMissing ||
      hasEmptyOtherLabel
    )
      return;
    setIsSubmitting(true);
    try {
      const otherAdditionalCosts = isSingleBlankItem
        ? []
        : otherItems
            .filter((i) => i.label.trim())
            .map((i) => ({ description: i.label, amount: parseFloat(i.price) || 0 }));

      let scanCostsVal: number | undefined;
      let panoCosts: number | undefined;
      let lidarCosts: number | undefined;
      let timeMaterialsFlightCosts: number | undefined;
      let gcpCosts: number | undefined;
      let gcpMaterialsCosts: number | undefined;
      let equipmentLeasingExpenses: number | undefined;

      if (isFixedFee) {
        const sectionCost = (id: string) => {
          const sec = costSections.find((s) => s.id === id);
          return sec ? sectionTotal(sec) : undefined;
        };
        scanCostsVal = sectionCost('scan');
        panoCosts = sectionCost('pano');
        lidarCosts = sectionCost('lidar');
        gcpCosts = gcpServicesTotal || undefined;
        gcpMaterialsCosts = gcpMaterialsTotal > 0 ? gcpMaterialsTotal : undefined;
        equipmentLeasingExpenses = equipmentTotal;
      } else {
        timeMaterialsFlightCosts = timeMaterialsDateGroups.reduce(
          (s, g) => s + parseFloat(timeMaterialsDatePrices[g.dateKey]?.flightsCosts || '0'),
          0,
        );
        gcpCosts = timeMaterialsDateGroups.reduce((s, g) => s + g.gcpItemsTotal, 0);
        gcpMaterialsCosts = gcpMaterialsTotal > 0 ? gcpMaterialsTotal : undefined;
        equipmentLeasingExpenses = equipmentTotal;
      }

      // Per-mission line items + T&M day entries for the consultant-invoices-api. GCP
      // materials/mileage ride on each GCP mission's `extraCosts`.
      // Looked up by the same key the rows are stored under — `item.gcpServiceId ?? item.id`, see
      // SubmitInvoice.tsx and TimeMaterialsCostBlock.tsx. Matching on gcpServiceId alone would drop
      // whatever the pilot typed on an item that arrived without one: the amount would still land in
      // the `gcpMaterialsCosts` aggregate but on no mission's extraCosts.
      const gcpExtraCostsFor = (item: { id: string; gcpServiceId?: string }) =>
        toGcpExtraCosts(gcpMaterialEntries[item.gcpServiceId ?? item.id]);

      let associatedMissions: AssociatedMissionPayload[];
      let timeMaterialsEntries: TimeMaterialsEntryPayload[] | undefined;

      if (isFixedFee) {
        // Fixed fee: every priced mission is a line item with its own cost.
        associatedMissions = costSections.flatMap((section) =>
          section.items
            .filter((item) => item.status !== 'failed' && !!item.airtableRecordId)
            .map((item) => {
              const isGcp = section.id === 'gcp_services';
              // Through the hook rather than off the raw price map: the same resolution the section
              // total and the row use, so what's billed is what the pilot was shown (a rate-set row
              // bills its calculated amount, never a seeded override).
              const cost = isGcp ? getGcpItemPrice(item.id, item.amount ?? 0) : item.amount ?? 0;
              const extraCosts = isGcp ? gcpExtraCostsFor(item) : undefined;
              const costBasis = isGcp
                ? toGcpCostBasis(item.consultantGcpServiceHourlyRate, item.consultantGcpServiceTimeInHours)
                : undefined;

              return {
                kind: item.category ?? 'other',
                airtableRecordId: item.airtableRecordId as string,
                date: item.flightDateIso,
                cost,
                itemCount: item.itemCount ?? 1,
                account: item.customer,
                project: item.siteName,
                airtableProjectRecordId: item.airtableProjectRecordId,
                ...(costBasis ? { costBasis } : {}),
                ...(extraCosts ? { extraCosts } : {}),
              };
            }),
        );
      } else {
        // Time & Materials: list ALL services (scans/panoramas/lidar + GCP) as the canonical set of
        // work performed. Scan/pano/lidar ride along at cost 0 (they're billed per-day via
        // timeMaterialsEntries below, so a real cost here would double-count in the service's
        // calculatedTotals); GCP keeps its real per-item cost.
        associatedMissions = timeMaterialsDateGroups.flatMap((g) => {
          const scanPanoLidar: AssociatedMissionPayload[] = g.scanPanoLidarItems
            .filter((item) => !item.isFailed && !!item.airtableRecordId)
            .map((item) => ({
              kind: item.category,
              airtableRecordId: item.airtableRecordId as string,
              date: toDateKey(item.flightDate),
              cost: 0,
              itemCount: item.itemCount ?? 1,
              account: item.account,
              project: item.project,
              airtableProjectRecordId: item.airtableProjectRecordId,
            }));

          const gcp: AssociatedMissionPayload[] = g.gcpItems
            .filter((item) => !item.isFailed && !!item.airtableRecordId)
            .map((item) => {
              const cost = getTmGcpItemPrice(item.id, tmGcpDefaultAmountById.get(item.id) ?? 0);
              const extraCosts = gcpExtraCostsFor(item);
              const costBasis = toGcpCostBasis(
                item.consultantGcpServiceHourlyRate,
                item.consultantGcpServiceTimeInHours,
              );

              return {
                kind: 'gcp_services',
                airtableRecordId: item.airtableRecordId,
                date: toDateKey(item.flightDate),
                cost,
                itemCount: item.itemCount ?? 1,
                account: item.account,
                project: item.project,
                airtableProjectRecordId: item.airtableProjectRecordId,
                ...(costBasis ? { costBasis } : {}),
                ...(extraCosts ? { extraCosts } : {}),
              };
            });

          return [...scanPanoLidar, ...gcp];
        });

        timeMaterialsEntries = timeMaterialsDateGroups.flatMap((g) => {
          const prices = timeMaterialsDatePrices[g.dateKey];
          // A fresh submit bills only billable work, so failed missions are left out of both id
          // sets (matching how associatedMissions is built above) and out of the GCP amounts.
          const billableGcpItems = g.gcpItems.filter((item) => !item.isFailed);

          return buildTimeMaterialsDayEntries({
            dateKey: g.dateKey,
            flights: parseFloat(prices?.flightsCosts || '0') || 0,
            gcpServices: g.gcpItemsTotal,
            gcpMaterials: sumGcpExtraCosts(billableGcpItems.map(gcpExtraCostsFor)),
            mileage: parseFloat(prices?.mileage || '0') || 0,
            flightRefIds: collectMissionRefIds(g.scanPanoLidarItems.filter((item) => !item.isFailed)),
            gcpRefIds: collectMissionRefIds(billableGcpItems),
          });
        });
      }

      await getPilotApiService().sendInvoice({
        startDate: dateRange?.[0]?.format('YYYY-MM-DD') ?? '',
        endDate: dateRange?.[1]?.format('YYYY-MM-DD') ?? '',
        scanCosts: scanCostsVal,
        panoCosts,
        lidarCosts,
        timeMaterialsFlightCosts,
        gcpCosts,
        gcpMaterialsCosts,
        equipmentLeasingExpenses,
        additionalCosts: otherAdditionalCosts.length ? otherAdditionalCosts : undefined,
        associatedMissions,
        timeMaterialsEntries: timeMaterialsEntries?.length ? timeMaterialsEntries : undefined,
        paymentLink: paymentMethod === 'link' ? paymentLink || undefined : undefined,
        bankDetails: paymentMethod === 'bank' ? bankDetails || undefined : undefined,
        invoiceLink: uploadedPdfLink,
        invoiceFileName: pdfFile?.name,
        invoiceFileSize: pdfFile?.size,
        invoiceFileType: pdfFile?.type,
      });
      await onSubmit?.({});
      message.success({ title: 'Invoice submitted', closable: true, theme: 'dark' });
      onBack?.();
    } catch (err) {
      Logger.error(err as Error | string);
      message.error({
        title: 'Failed to submit invoice',
        description: 'Please try again or contact support.',
        closable: true,
        theme: 'dark',
      });
      setIsSubmitting(false);
    }
  }

  return {
    // state
    expanded,
    otherItems,
    gcpItemPrices,
    updateGcpItemPrice,
    gcpMaterialsHandlers,
    equipmentLeasingPrice,
    setEquipmentLeasingPrice,
    pdfFile,
    isPdfUploading,
    uploadedPdfLink,
    paymentMethod,
    setPaymentMethod,
    paymentLink,
    setPaymentLink,
    bankDetails,
    setBankDetails,
    submitAttempted,
    submittedOtherIds,
    isSubmitting,
    showLeaveDialog,
    // T&M
    timeMaterialsDateGroups,
    timeMaterialsDatePrices,
    setDatePrice,
    tmGcpItemPrices,
    updateTmGcpItemPrice,
    tmGcpDefaultAmountById,
    // derived
    filteredCosts,
    costSections,
    billableDates,
    unprocessedDates,
    otherTotal,
    gcpMaterialsTotal,
    gcpServicesTotal,
    equipmentTotal,
    sectionsTotal,
    timeMaterialsTotal,
    grandTotal,
    isDirty,
    isNegativeTotal,
    isZeroTotal,
    isUploadMissing,
    isPaymentLinkMissing,
    isPaymentLinkInvalid,
    isBankDetailsMissing,
    isSingleBlankItem,
    hasEmptyOtherLabel,
    displayTotal,
    // handlers
    toggleSection,
    addOtherItem,
    removeOtherItem,
    updateOtherItemLabel,
    updateOtherItemPrice,
    handlePdfChange,
    handleBack,
    handleLeaveConfirm,
    handleLeaveCancel,
    handleSubmit,
  };
}
